'use client';

import { useState } from 'react';
import styled from 'styled-components';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Breadcrumbs from '@/components/Breadcrumbs';
import { COLORS } from '@/constants/colors';
import { TYPOGRAPHY, FONT_SIZE } from '@/constants/typography';
import { SPACING } from '@/constants/spacing';
import { BREAKPOINTS } from '@/constants/breakpoints';

export default function ContactUsPage() {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    message: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);

  const validateForm = (data: typeof formData) => {
    const newErrors: Record<string, string> = {};

    if (!data.first_name || data.first_name.trim().length < 2) {
      newErrors.first_name = 'First name must be at least 2 characters';
    } else if (data.first_name.length > 50) {
      newErrors.first_name = 'First name must be less than 50 characters';
    }

    if (!data.last_name || data.last_name.trim().length < 2) {
      newErrors.last_name = 'Last name must be at least 2 characters';
    } else if (data.last_name.length > 50) {
      newErrors.last_name = 'Last name must be less than 50 characters';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!data.email || !emailRegex.test(data.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!data.message || data.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    } else if (data.message.length > 2000) {
      newErrors.message = 'Message must be less than 2000 characters';
    }

    return newErrors;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validateForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors({});
    setSubmitStatus(null);

    try {
      const res = await fetch('https://api.greenfilter.com/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!res.ok) throw new Error('Failed to submit form');

      setSubmitStatus('success');
      setFormData({ first_name: '', last_name: '', email: '', message: '' });
    } catch (error: any) {
      setSubmitStatus('error');
      setErrors({ submit: error.response?.data?.message || 'Failed to send message. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Header handleClickIndex={() => {}} />
      <div className="header-gap-creator"></div>

      <PageContainer as="main">
        <Breadcrumbs items={[{ label: 'Contact Us' }]} />
        <ContentWrapper>
          {/* Page Header */}
          <PageHeader>
            <PageTitle>Get in Touch</PageTitle>
            <PageSubtitle>
              Have a question about our filters? Need help finding the right product for your vehicle?
              We're here to help.
            </PageSubtitle>
          </PageHeader>

          {/* Two Column Layout */}
          <MainContent>
            {/* Contact Information Column */}
            <ContactInfoColumn>
              <ContactCardsWrapper>
                <ContactCard>
                  <IconWrapper>
                    <PhoneIcon />
                  </IconWrapper>
                  <CardContent>
                    <CardTitle>Phone</CardTitle>
                    <CardDetail href="tel:+17244302050">+1 724.430.2050</CardDetail>
                    <CardSubtext>Mon-Fri, 9am-5pm EST</CardSubtext>
                  </CardContent>
                </ContactCard>

                <ContactCard>
                  <IconWrapper>
                    <EmailIcon />
                  </IconWrapper>
                  <CardContent>
                    <CardTitle>Email</CardTitle>
                    <CardDetail href="mailto:csr@greenfilter.com">csr@greenfilter.com</CardDetail>
                    <CardSubtext>We'll respond within 24 hours</CardSubtext>
                  </CardContent>
                </ContactCard>

                <ContactCard>
                  <IconWrapper>
                    <LocationIcon />
                  </IconWrapper>
                  <CardContent>
                    <CardTitle>Address</CardTitle>
                    <CardDetail>Green Filter USA</CardDetail>
                    <CardSubtext>Pennsylvania, United States</CardSubtext>
                  </CardContent>
                </ContactCard>
              </ContactCardsWrapper>
            </ContactInfoColumn>

            {/* Contact Form Column */}
            <FormColumn>
              <FormWrapper>
                {submitStatus === 'success' && (
                  <StatusMessage $variant="success">
                    <StatusIcon $variant="success">✓</StatusIcon>
                    <div>
                      <StatusTitle>Message sent successfully!</StatusTitle>
                      <StatusText>We'll get back to you within 24 hours.</StatusText>
                    </div>
                  </StatusMessage>
                )}

                {errors.submit && (
                  <StatusMessage $variant="error">
                    <StatusIcon $variant="error">✕</StatusIcon>
                    <div>{errors.submit}</div>
                  </StatusMessage>
                )}

                <form onSubmit={handleSubmit}>
                  <FormRow>
                    <FormField>
                      <Label htmlFor="first_name">
                        First Name <Required>*</Required>
                      </Label>
                      <Input
                        id="first_name"
                        type="text"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleChange}
                        $hasError={!!errors.first_name}
                        placeholder="John"
                      />
                      {errors.first_name && <FieldError>{errors.first_name}</FieldError>}
                    </FormField>

                    <FormField>
                      <Label htmlFor="last_name">
                        Last Name <Required>*</Required>
                      </Label>
                      <Input
                        id="last_name"
                        type="text"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleChange}
                        $hasError={!!errors.last_name}
                        placeholder="Doe"
                      />
                      {errors.last_name && <FieldError>{errors.last_name}</FieldError>}
                    </FormField>
                  </FormRow>

                  <FormField>
                    <Label htmlFor="email">
                      Email Address <Required>*</Required>
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      $hasError={!!errors.email}
                      placeholder="john.doe@example.com"
                    />
                    {errors.email && <FieldError>{errors.email}</FieldError>}
                  </FormField>

                  <FormField>
                    <Label htmlFor="message">
                      Message <Required>*</Required>
                    </Label>
                    <TextArea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      $hasError={!!errors.message}
                      placeholder="Tell us how we can help you..."
                    />
                    {errors.message && <FieldError>{errors.message}</FieldError>}
                    <CharCount $isNearLimit={formData.message.length > 1800}>
                      {formData.message.length} / 2000
                    </CharCount>
                  </FormField>

                  <SubmitButton type="submit" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <Spinner />
                        Sending...
                      </>
                    ) : (
                      <>
                        <SendIcon />
                        Send Message
                      </>
                    )}
                  </SubmitButton>
                </form>
              </FormWrapper>
            </FormColumn>
          </MainContent>
        </ContentWrapper>
      </PageContainer>

      <Footer />
    </>
  );
}

// Styled Components
const PageContainer = styled.div`
  background: linear-gradient(180deg, ${COLORS.white} 0%, ${COLORS.bgGray} 100%);
  min-height: calc(100vh - 110px);
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${SPACING.xl} ${SPACING.lg};

  @media (max-width: ${BREAKPOINTS.mobile}) {
    padding: ${SPACING.lg} ${SPACING.md};
  }
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: ${SPACING.xl};

  @media (max-width: ${BREAKPOINTS.mobile}) {
    margin-bottom: ${SPACING.md};
  }
`;

const PageTitle = styled.h1`
  ${TYPOGRAPHY.h1}
  color: ${COLORS.textPrimary};
  margin-top: 0;
  margin-bottom: ${SPACING.sm};

  @media (max-width: ${BREAKPOINTS.mobile}) {
    font-size: ${FONT_SIZE.heading.md};
    margin-bottom: ${SPACING.xs};
  }
`;

const PageSubtitle = styled.p`
  ${TYPOGRAPHY.body}
  color: ${COLORS.textSecondary};
  max-width: 700px;
  margin: 0 auto;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    font-size: ${FONT_SIZE.body.sm};
  }
`;

const MainContent = styled.div`
  display: grid;
  grid-template-columns: 380px 1fr;
  gap: ${SPACING.xl};

  @media (max-width: ${BREAKPOINTS.tablet}) {
    grid-template-columns: 1fr;
    gap: ${SPACING.lg};
  }
`;

const ContactInfoColumn = styled.div`
  @media (max-width: ${BREAKPOINTS.tablet}) {
    order: 2;
  }
`;

const ContactCardsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm};

  @media (max-width: ${BREAKPOINTS.mobile}) {
    gap: ${SPACING.xs};
  }
`;

const ContactCard = styled.div`
  background: ${COLORS.white};
  border: 1px solid ${COLORS.borderGray};
  border-radius: 8px;
  padding: ${SPACING.md};
  display: flex;
  gap: ${SPACING.md};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${COLORS.brandGreen};
  }

  @media (max-width: ${BREAKPOINTS.mobile}) {
    padding: ${SPACING.sm};
    gap: ${SPACING.sm};
  }
`;

const IconWrapper = styled.div`
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, ${COLORS.brandGreen} 0%, ${COLORS.successAlt} 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 24px;
    height: 24px;
    color: ${COLORS.white};
  }

  @media (max-width: ${BREAKPOINTS.mobile}) {
    width: 40px;
    height: 40px;

    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

const CardContent = styled.div`
  flex: 1;
`;

const CardTitle = styled.div`
  ${TYPOGRAPHY.label}
  color: ${COLORS.textSecondary};
  margin-bottom: ${SPACING.xs};
  text-transform: uppercase;
  font-size: ${FONT_SIZE.body.xxs};
  letter-spacing: 0.5px;
`;

const CardDetail = styled.a`
  ${TYPOGRAPHY.h5}
  color: ${COLORS.textPrimary};
  margin-bottom: ${SPACING.xs};
  display: block;
  text-decoration: none;

  &:hover {
    color: ${COLORS.brandGreen};
  }
`;

const CardSubtext = styled.div`
  ${TYPOGRAPHY.caption}
  color: ${COLORS.textMuted};
`;

const FormColumn = styled.div`
  @media (max-width: ${BREAKPOINTS.tablet}) {
    order: 1;
  }
`;

const FormWrapper = styled.div`
  background: ${COLORS.white};
  border-radius: 8px;
  padding: ${SPACING.xl};
  border: 1px solid ${COLORS.borderGray};

  @media (max-width: ${BREAKPOINTS.mobile}) {
    padding: ${SPACING.md};
  }
`;

const StatusMessage = styled.div<{ $variant: 'success' | 'error' }>`
  background: linear-gradient(
    135deg,
    ${(props) => (props.$variant === 'success' ? COLORS.success : COLORS.error)} 0%,
    ${(props) => (props.$variant === 'success' ? COLORS.successDark : COLORS.errorDark)} 100%
  );
  color: ${COLORS.white};
  padding: ${SPACING.md} ${SPACING.lg};
  border-radius: 12px;
  margin-bottom: ${SPACING.lg};
  display: flex;
  align-items: center;
  gap: ${SPACING.md};
  animation: slideIn 0.3s ease;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: ${BREAKPOINTS.mobile}) {
    padding: ${SPACING.sm} ${SPACING.md};
    margin-bottom: ${SPACING.md};
    gap: ${SPACING.sm};
  }
`;

const StatusIcon = styled.div<{ $variant: 'success' | 'error' }>`
  width: ${(props) => (props.$variant === 'success' ? '40px' : '32px')};
  height: ${(props) => (props.$variant === 'success' ? '40px' : '32px')};
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${(props) => (props.$variant === 'success' ? FONT_SIZE.heading.sm : FONT_SIZE.heading.xs)};
  font-weight: 700;
  flex-shrink: 0;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    width: ${(props) => (props.$variant === 'success' ? '32px' : '28px')};
    height: ${(props) => (props.$variant === 'success' ? '32px' : '28px')};
    font-size: ${(props) => (props.$variant === 'success' ? FONT_SIZE.heading.xs : FONT_SIZE.body.md)};
  }
`;

const StatusTitle = styled.div`
  ${TYPOGRAPHY.h5}
  margin-bottom: ${SPACING.xs};

  @media (max-width: ${BREAKPOINTS.mobile}) {
    font-size: ${FONT_SIZE.body.md};
  }
`;

const StatusText = styled.div`
  ${TYPOGRAPHY.bodyXS}
  opacity: 0.95;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    font-size: ${FONT_SIZE.body.xxs};
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${SPACING.md};

  @media (max-width: ${BREAKPOINTS.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const FormField = styled.div`
  margin-bottom: ${SPACING.md};

  @media (max-width: ${BREAKPOINTS.mobile}) {
    margin-bottom: ${SPACING.sm};
  }
`;

const Label = styled.label`
  ${TYPOGRAPHY.label}
  color: ${COLORS.textPrimary};
  display: block;
  margin-bottom: ${SPACING.xs};
`;

const Required = styled.span`
  color: ${COLORS.error};
`;

const Input = styled.input<{ $hasError?: boolean }>`
  ${TYPOGRAPHY.body}
  width: 100%;
  padding: ${SPACING.sm} ${SPACING.md};
  border: 1px solid ${(props) => (props.$hasError ? COLORS.error : COLORS.borderGray)};
  border-radius: 6px;
  background: ${COLORS.white};
  color: ${COLORS.textPrimary};
  transition: all 0.2s ease;

  &::placeholder {
    color: ${COLORS.textMuted};
  }

  &:focus {
    outline: none;
    border-color: ${(props) => (props.$hasError ? COLORS.error : COLORS.brandGreen)};
  }

  &:disabled {
    background: ${COLORS.bgGray};
    cursor: not-allowed;
  }
`;

const TextArea = styled.textarea<{ $hasError?: boolean }>`
  ${TYPOGRAPHY.body}
  width: 100%;
  padding: ${SPACING.sm} ${SPACING.md};
  border: 1px solid ${(props) => (props.$hasError ? COLORS.error : COLORS.borderGray)};
  border-radius: 6px;
  background: ${COLORS.white};
  color: ${COLORS.textPrimary};
  resize: vertical;
  min-height: 100px;
  transition: all 0.2s ease;
  font-family: inherit;

  &::placeholder {
    color: ${COLORS.textMuted};
  }

  &:focus {
    outline: none;
    border-color: ${(props) => (props.$hasError ? COLORS.error : COLORS.brandGreen)};
  }
`;

const CharCount = styled.div<{ $isNearLimit?: boolean }>`
  ${TYPOGRAPHY.caption}
  color: ${(props) => (props.$isNearLimit ? COLORS.error : COLORS.textMuted)};
  text-align: right;
  margin-top: ${SPACING.xs};
`;

const FieldError = styled.div`
  ${TYPOGRAPHY.caption}
  color: ${COLORS.error};
  margin-top: ${SPACING.xs};
  display: flex;
  align-items: center;
  gap: 4px;

  &::before {
    content: '⚠';
  }
`;

const SubmitButton = styled.button`
  ${TYPOGRAPHY.button}
  width: 100%;
  padding: ${SPACING.md} ${SPACING.xl};
  background: linear-gradient(135deg, ${COLORS.brandGreen} 0%, ${COLORS.successAlt} 100%);
  color: ${COLORS.white};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${SPACING.sm};
  margin-top: ${SPACING.lg};

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, ${COLORS.successAlt} 0%, ${COLORS.brandGreen} 100%);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 173, 35, 0.3);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    background: ${COLORS.gray600};
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const Spinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: ${COLORS.white};
  border-radius: 50%;
  animation: spin 0.6s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

// Icon Components
const PhoneIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
    />
  </svg>
);

const EmailIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
    />
  </svg>
);

const LocationIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
    />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const SendIcon = () => (
  <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
    />
  </svg>
);
